import * as React from 'react';
import { Accordion, Paper, ScrollArea, Stack, Table, Text, Title } from '@mantine/core';
import { useRoomContext } from '@livekit/components-react';
import { type LocalTrackPublication, type LogLevel, type RemoteTrackPublication, setLogLevel, Track } from 'livekit-client';
import { useTranslation } from 'react-i18next';
import { tinykeys } from 'tinykeys';

import { i18n } from '@/i18n';
import { isEditableTarget } from '@/shared/is-editable-target';

declare global {
    var __lk_room: unknown;
}

export const useDebugMode = ({ logLevel }: { logLevel?: LogLevel }) => {
    const room = useRoomContext();

    React.useEffect(() => {
        setLogLevel(logLevel ?? 'debug');

        globalThis.__lk_room = room;

        return () => {
            globalThis.__lk_room = undefined;
        };
    }, [room, logLevel]);
};

function TrackTable({
    publication,
    rows,
}: Readonly<{
    publication: LocalTrackPublication | RemoteTrackPublication;
    rows: [string, React.ReactNode][];
}>) {
    return (
        <Stack gap="var(--spacing-1)">
            <Text fs="italic" size="sm">
                {publication.source.toString()}{' '}
                <Text inherit span c="dimmed">
                    {publication.trackSid}
                </Text>
            </Text>
            <Table verticalSpacing="var(--spacing-0-5)" withRowBorders={false}>
                <Table.Tbody>
                    {rows.map(([key, value]) => (
                        <Table.Tr key={key}>
                            <Table.Td c="dimmed">{key}</Table.Td>
                            <Table.Td>{value}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Stack>
    );
}

function trackStatus(t: RemoteTrackPublication): string {
    if (t.isSubscribed) {
        return t.isEnabled ? i18n.t('settings:debug.enabled') : i18n.t('settings:debug.disabled');
    }
    return i18n.t('settings:debug.unsubscribed');
}

export const DebugMode = ({ logLevel }: { logLevel?: LogLevel }) => {
    const { t } = useTranslation('settings');
    const room = useRoomContext();
    const [isOpen, setIsOpen] = React.useState(false);
    const [, setRender] = React.useState({});
    const [roomSid, setRoomSid] = React.useState('');

    React.useEffect(() => {
        room.getSid().then(setRoomSid, () => undefined);
    }, [room]);

    useDebugMode({ logLevel });

    React.useEffect(() => {
        const unsubscribe = tinykeys(globalThis.window, {
            'Shift+D': (event) => {
                if (isEditableTarget(event.target)) return;
                setIsOpen((open) => !open);
            },
        });
        return unsubscribe;
    }, []);

    React.useEffect(() => {
        if (!isOpen) return;
        const interval = setInterval(() => {
            setRender({});
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const lp = room.localParticipant;

    return (
        <Paper
            mah="80%"
            p="md"
            pos="absolute"
            right="var(--spacing-3)"
            shadow="md"
            style={{ zIndex: 20, display: 'flex', flexDirection: 'column' }}
            top="var(--spacing-3)"
            w="var(--spacing-96)"
        >
            <ScrollArea.Autosize mah="100%">
                <Stack gap="sm">
                    <Title order={5}>{t('debug.room', { name: room.name, sid: roomSid })}</Title>
                    <Accordion multiple defaultValue={['local']} variant="contained">
                        <Accordion.Item value="local">
                            <Accordion.Control>{t('debug.localParticipant', { identity: lp.identity })}</Accordion.Control>
                            <Accordion.Panel>
                                <Stack gap="sm">
                                    <Text fw={600} size="sm">
                                        {t('debug.publishedTracks')}
                                    </Text>
                                    {[...lp.trackPublications.values()].map((publication) => (
                                        <TrackTable
                                            key={publication.trackSid}
                                            publication={publication}
                                            rows={[
                                                [
                                                    t('debug.type'),
                                                    publication.kind === Track.Kind.Video && publication.track?.dimensions
                                                        ? `${publication.kind} ${String(publication.track.dimensions.width)}x${String(publication.track.dimensions.height)}`
                                                        : publication.kind,
                                                ],
                                                [
                                                    t('debug.bitrate'),
                                                    publication.track
                                                        ? `${String(Math.ceil(publication.track.currentBitrate / 1000))} kbps`
                                                        : '—',
                                                ],
                                            ]}
                                        />
                                    ))}
                                    <Text fw={600} size="sm">
                                        {t('debug.permissions')}
                                    </Text>
                                    <Table verticalSpacing="var(--spacing-0-5)" withRowBorders={false}>
                                        <Table.Tbody>
                                            {lp.permissions &&
                                                Object.entries(lp.permissions).map(([key, val]) => (
                                                    <Table.Tr key={key}>
                                                        <Table.Td c="dimmed">{key}</Table.Td>
                                                        <Table.Td>{Array.isArray(val) ? val.join(', ') : String(val)}</Table.Td>
                                                    </Table.Tr>
                                                ))}
                                        </Table.Tbody>
                                    </Table>
                                </Stack>
                            </Accordion.Panel>
                        </Accordion.Item>
                        <Accordion.Item value="remote">
                            <Accordion.Control>{t('debug.remoteParticipants')}</Accordion.Control>
                            <Accordion.Panel>
                                <Accordion multiple variant="contained">
                                    {[...room.remoteParticipants.values()].map((p) => (
                                        <Accordion.Item key={p.sid} value={p.sid}>
                                            <Accordion.Control>{p.identity}</Accordion.Control>
                                            <Accordion.Panel>
                                                <Stack gap="sm">
                                                    {[...p.trackPublications.values()].map((publication) => (
                                                        <TrackTable
                                                            key={publication.trackSid}
                                                            publication={publication}
                                                            rows={[
                                                                [
                                                                    t('debug.type'),
                                                                    publication.kind === Track.Kind.Video &&
                                                                    publication.dimensions
                                                                        ? `${publication.kind} ${String(publication.dimensions.width)}x${String(publication.dimensions.height)}`
                                                                        : publication.kind,
                                                                ],
                                                                [t('debug.status'), trackStatus(publication)],
                                                                ...(publication.track
                                                                    ? ([
                                                                          [
                                                                              t('debug.bitrate'),
                                                                              `${String(Math.ceil(publication.track.currentBitrate / 1000))} kbps`,
                                                                          ],
                                                                      ] as [string, React.ReactNode][])
                                                                    : []),
                                                            ]}
                                                        />
                                                    ))}
                                                </Stack>
                                            </Accordion.Panel>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            </Accordion.Panel>
                        </Accordion.Item>
                    </Accordion>
                </Stack>
            </ScrollArea.Autosize>
        </Paper>
    );
};
